import { z } from 'zod';

export const CONFIG_OVERRIDE_KEY = 'orderhub:config-override';

/**
 * Dev-only cookie carrying a Convex URL override for server-side rendering,
 * where localStorage is unreachable (e.g. consumer-web's Astro SSR). Set per
 * browser context by e2e; production builds ignore it.
 */
export const CONVEX_URL_OVERRIDE_COOKIE = 'orderhub-convex-url-override';

const overrideSchema = z.object({
  convexUrl: z.url().optional(),
  cdnUrl: z.url().optional(),
  convexSiteUrl: z.url().optional(),
});

export type WebConfigOverride = z.output<typeof overrideSchema>;

/**
 * Reads the `orderhub:config-override` localStorage entry and returns
 * validated partial overrides for backend-related URLs. Returns `null`
 * when no override is set or when the stored value is invalid.
 *
 * Intended for e2e tests and local development where the frontend
 * needs to talk to a different backend (e.g. local SQLite Convex).
 */
export const getConfigOverride = (): WebConfigOverride | null => {
  try {
    const raw = localStorage.getItem(CONFIG_OVERRIDE_KEY);
    if (!raw) return null;
    const parsed = overrideSchema.parse(JSON.parse(raw));
    if (Object.keys(parsed).length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
};

/** Applies the override on top of a build-time default, returning the override value if set. */
export const resolveWithOverride = (
  buildTimeValue: string | undefined,
  overrideValue: string | undefined,
): string => {
  const resolved = overrideValue ?? buildTimeValue;
  if (!resolved)
    throw new Error('resolveWithOverride: build-time value is not set and no override provided');
  return resolved;
};
